import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material'
import { PageLayout, PageHeader } from '../../components'
import { StatusChip, LoadingSpinner } from '../../components/atoms'
import { authenticatedFetchJson } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import useBillingProfilesData from '../../hooks/useBillingProfilesData'
import logger from '../../utils/logger'
import styles from './AdminBillingProfiles.module.scss'

const NEEDS_ATTENTION_LABELS = {
  no_billing_contact: 'Add billing contact',
  missing_primary_billing_contact: 'Add billing contact',
}

const PAGE_SIZE = 25

const SortableHeader = ({ field, label, sortField, sortDirection, onSort }) => (
  <th
    className={styles.sortable}
    aria-sort={
      sortField === field ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'
    }
  >
    <button type="button" className={styles.sortButton} onClick={() => onSort(field)}>
      {label} {sortField === field && (sortDirection === 'asc' ? '↑' : '↓')}
    </button>
  </th>
)

const AdminBillingProfiles = ({ embedded = false }) => {
  const navigate = useNavigate()
  const { customers, loading, error, refetch } = useBillingProfilesData()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState('account_name')
  const [sortDirection, setSortDirection] = useState('asc')
  const [page, setPage] = useState(1)

  const [resolveDialogOpen, setResolveDialogOpen] = useState(false)
  const [resolveCustomer, setResolveCustomer] = useState(null)
  const [stripeIdInput, setStripeIdInput] = useState('')
  const [resolving, setResolving] = useState(false)
  const [resolveError, setResolveError] = useState(null)

  const filteredCustomers = useMemo(() => {
    let filtered = customers

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (c) =>
          c.account_name?.toLowerCase().includes(term) ||
          c.stripe_customer_id?.toLowerCase().includes(term),
      )
    }

    return [...filtered].sort((a, b) => {
      const aPending = a.stripe_setup_status ? 0 : 1
      const bPending = b.stripe_setup_status ? 0 : 1
      if (aPending !== bPending) return aPending - bPending
      const aVal = a[sortField] || ''
      const bVal = b[sortField] || ''
      const cmp = String(aVal).localeCompare(String(bVal))
      return sortDirection === 'asc' ? cmp : -cmp
    })
  }, [customers, searchTerm, sortField, sortDirection])

  const totalPages = Math.ceil(filteredCustomers.length / PAGE_SIZE)
  const paginatedCustomers = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return filteredCustomers.slice(start, start + PAGE_SIZE)
  }, [filteredCustomers, page])

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value)
    setPage(1)
  }, [])

  const handleSort = useCallback(
    (field) => {
      setSortDirection((prev) => (sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'))
      setSortField(field)
      setPage(1)
    },
    [sortField],
  )

  const openResolveDialog = useCallback((customer) => {
    setResolveCustomer(customer)
    setStripeIdInput('')
    setResolveError(null)
    setResolveDialogOpen(true)
  }, [])

  const closeResolveDialog = useCallback(() => {
    setResolveDialogOpen(false)
    setResolveCustomer(null)
    setStripeIdInput('')
    setResolveError(null)
  }, [])

  const handleResolveSubmit = useCallback(async () => {
    if (!resolveCustomer) return
    setResolving(true)
    setResolveError(null)
    try {
      const body =
        resolveCustomer.stripe_setup_status === 'requested_existing'
          ? { stripe_customer_id: stripeIdInput.trim() }
          : { action: 'create_new' }

      await authenticatedFetchJson(apiEndpoints.billingResolveStripeSetup(resolveCustomer.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      closeResolveDialog()
      refetch()
    } catch (err) {
      logger.error('Failed to resolve Stripe setup:', err)
      setResolveError(err.message || 'Failed to resolve Stripe setup')
    } finally {
      setResolving(false)
    }
  }, [resolveCustomer, stripeIdInput, closeResolveDialog, refetch])

  const goToCustomer = useCallback(
    (customer) => {
      if (customer.stripe_setup_status) {
        openResolveDialog(customer)
        return
      }
      if (customer.client_account_id) {
        navigate(`/billing/admin/view/${customer.client_account_id}`)
      }
    },
    [navigate, openResolveDialog],
  )

  const handleRowKeyDown = useCallback(
    (e, customer) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        goToCustomer(customer)
      }
    },
    [goToCustomer],
  )

  if (loading) {
    if (embedded) return <LoadingSpinner message="Loading billing profiles..." />
    return (
      <PageLayout>
        <PageHeader title="Billing Profiles" subtitle="Manage client billing setup status" />
        <LoadingSpinner message="Loading billing profiles..." />
      </PageLayout>
    )
  }

  if (error) {
    if (embedded)
      return (
        <div className={styles.errorState}>
          <p>{error}</p>
        </div>
      )
    return (
      <PageLayout>
        <PageHeader title="Billing Profiles" subtitle="Manage client billing setup status" />
        <div className={styles.errorState}>
          <p>{error}</p>
        </div>
      </PageLayout>
    )
  }

  const content = (
    <>
      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <div className={styles.searchWrapper}>
            <FontAwesomeIcon icon="search" className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              data-testid="search-input"
            />
          </div>
        </div>
        <span className={styles.resultCount}>
          {filteredCustomers.length} of {customers.length} clients
        </span>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <SortableHeader
                field="account_name"
                label="Client Name"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <th>Country</th>
              <th>Stripe Linked</th>
              <th>Payment Method</th>
              <th>Contacts</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {paginatedCustomers.length === 0 ? (
              <tr>
                <td colSpan={6} className={styles.emptyRow}>
                  {searchTerm ? 'No matching billing profiles' : 'No billing profiles found'}
                </td>
              </tr>
            ) : (
              paginatedCustomers.map((customer) => (
                <tr
                  key={customer.id}
                  data-testid="billing-profile-row"
                  className={`${styles.clickableRow} ${customer.stripe_setup_status ? styles.pendingSetupRow : ''}`}
                  tabIndex={0}
                  role="button"
                  onClick={() => goToCustomer(customer)}
                  onKeyDown={(e) => handleRowKeyDown(e, customer)}
                >
                  <td>
                    <div className={styles.clientName}>
                      {customer.stripe_setup_status && (
                        <FontAwesomeIcon
                          icon="exclamation-circle"
                          className={styles.attentionIcon}
                        />
                      )}
                      <span>{customer.account_name}</span>
                      <FontAwesomeIcon icon="chevron-right" className={styles.chevron} />
                    </div>
                  </td>
                  <td data-testid="billing-profile-country">
                    {customer.country_code ? (
                      <StatusChip label={customer.country_code} color="neutral" size="small" />
                    ) : (
                      <span className={styles.mutedText}>—</span>
                    )}
                  </td>
                  <td>
                    <StatusChip
                      label={customer.stripe_customer_id ? 'Yes' : 'No'}
                      color={customer.stripe_customer_id ? 'success' : 'neutral'}
                      size="small"
                    />
                  </td>
                  <td>
                    <StatusChip
                      label={customer.payment_method_present ? 'Yes' : 'No'}
                      color={customer.payment_method_present ? 'success' : 'error'}
                      size="small"
                    />
                  </td>
                  <td>{customer.contact_count || 0}</td>
                  <td>
                    {customer.stripe_setup_status ? (
                      <StatusChip
                        label={
                          customer.stripe_setup_status === 'requested_existing'
                            ? 'Setup: Existing'
                            : 'Setup: New'
                        }
                        color="warning"
                        size="small"
                      />
                    ) : customer.needs_attention ? (
                      <StatusChip
                        label={
                          NEEDS_ATTENTION_LABELS[customer.needs_attention] || 'Needs attention'
                        }
                        color="warning"
                        size="small"
                      />
                    ) : (
                      <StatusChip label="OK" color="success" size="small" />
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination} data-testid="pagination">
          <button
            className={styles.pageButton}
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {page} of {totalPages}
          </span>
          <button
            className={styles.pageButton}
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
      {/* Resolve Stripe Setup Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={closeResolveDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--color-card-bg)',
            color: 'var(--color-text-primary)',
          },
        }}
      >
        <DialogTitle>Resolve Stripe Setup — {resolveCustomer?.account_name}</DialogTitle>
        <DialogContent>
          {resolveCustomer?.stripe_setup_status === 'requested_existing' ? (
            <>
              <p style={{ marginBottom: 16, color: 'var(--color-text-secondary)' }}>
                This client indicated they have an existing Stripe account. Enter their Stripe
                customer ID to link it.
              </p>
              <TextField
                label="Stripe Customer ID"
                placeholder="cus_..."
                value={stripeIdInput}
                onChange={(e) => setStripeIdInput(e.target.value)}
                fullWidth
                size="small"
                disabled={resolving}
                autoFocus
                inputProps={{ 'data-testid': 'stripe-id-input' }}
              />
            </>
          ) : (
            <p style={{ color: 'var(--color-text-secondary)' }}>
              This client requested a new Stripe setup. Click below to create a new Stripe customer
              for them.
            </p>
          )}
          {resolveError && (
            <p style={{ color: 'var(--color-status-error)', marginTop: 12 }}>{resolveError}</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResolveDialog} disabled={resolving}>
            Cancel
          </Button>
          <Button
            onClick={handleResolveSubmit}
            variant="contained"
            disabled={
              resolving ||
              (resolveCustomer?.stripe_setup_status === 'requested_existing' &&
                !stripeIdInput.trim())
            }
            data-testid="resolve-submit-btn"
            style={{
              backgroundColor:
                !resolving &&
                (resolveCustomer?.stripe_setup_status !== 'requested_existing' ||
                  stripeIdInput.trim())
                  ? 'var(--color-brand-primary)'
                  : undefined,
            }}
          >
            {resolving
              ? 'Processing...'
              : resolveCustomer?.stripe_setup_status === 'requested_existing'
                ? 'Link Customer'
                : 'Create Stripe Customer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )

  if (embedded) return content

  return (
    <PageLayout>
      <PageHeader title="Billing Profiles" subtitle="Click a client to manage their billing" />
      {content}
    </PageLayout>
  )
}

AdminBillingProfiles.propTypes = {
  embedded: PropTypes.bool,
}


export default AdminBillingProfiles
